import { orderBy, query } from 'firebase/firestore'
import {
  ExternalLink,
  Layers,
  Pencil,
  Plus,
  Trash,
  Trophy,
  UserRoundSearch,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useConfirmModal } from '@/components/ui/confirm-modal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  initializeWallStateIfNeeded,
  subscribeToWallState,
  updateWallState,
  type WallState,
} from '@/lib/firebase/activities'
import { collectionRef } from '@/lib/firebase/firestore'
import { listenToCollection } from '@/lib/firebase/utils'

import { useAddSessionModal } from './AddSessionModal'
import { useRenameSessionModal } from './RenameSessionModal'
import { WallControlTab } from './WallControlTab'
import { WallSettingsTab } from './WallSettingsTab'
import { WallWinnersTab } from './WallWinnersTab'

interface CheckinGuest {
  id: string
  name: string
  pictureUrl?: string
  maritalStatus?: string
}

const openDisplay = () => window.open('/wall-display', '_blank')

export function WallAdminPage() {
  const [wallState, setWallState] = useState<WallState | null>(null)
  const [allCheckins, setAllCheckins] = useState<CheckinGuest[]>([])

  const { AddSessionModal, setShowAddSessionModal } = useAddSessionModal()
  const { RenameSessionModal, setShowRenameSessionModal } =
    useRenameSessionModal()
  const { ConfirmModal, confirm } = useConfirmModal()

  const [renameSessionId, setRenameSessionId] = useState('')
  const [renameSessionName, setRenameSessionName] = useState('')

  const [localSettings, setLocalSettings] = useState<WallState['settings']>({
    spinDuration: 5,
    excludeWinners: true,
    autoClose: true,
    autoCloseDelay: 10,
    displayMethod: 'bubbles',
    maritalStatusFilter: [],
    backgroundImagePath: '',
    backgroundImageUrl: '',
  })

  const spinTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const settingsSyncedRef = useRef(false)
  const prevSessionIdRef = useRef<string | null>(null)

  useEffect(() => {
    initializeWallStateIfNeeded().catch(console.error)
    return subscribeToWallState((state) => {
      setWallState(state)
      if (!settingsSyncedRef.current) {
        settingsSyncedRef.current = true
        setLocalSettings(state.settings)
        // Clear stale isSpinning on first load — timer is local, so if it's
        // true here with no timer running it means a previous session crashed.
        if (state.isSpinning && !spinTimerRef.current) {
          updateWallState({ isSpinning: false }).catch(console.error)
        }
      }
    })
  }, [])

  // Sync localSettings when session switches
  useEffect(() => {
    if (wallState && wallState.currentSessionId !== prevSessionIdRef.current) {
      prevSessionIdRef.current = wallState.currentSessionId
      setLocalSettings(wallState.settings)
    }
  }, [wallState?.currentSessionId, wallState?.settings]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save settings when localSettings changes (after initial sync)
  useEffect(() => {
    if (settingsSyncedRef.current && wallState) {
      const currentId = wallState.currentSessionId || 'default'
      const activeSession = (wallState.sessions || []).find(
        (s) => s.id === currentId,
      )

      const isDifferent =
        JSON.stringify(wallState.settings) !== JSON.stringify(localSettings) ||
        (activeSession &&
          JSON.stringify(activeSession.settings) !==
            JSON.stringify(localSettings))

      if (isDifferent) {
        const updatedSessions = (wallState.sessions || []).map((s) => {
          if (s.id === currentId) {
            return { ...s, settings: localSettings }
          }
          return s
        })

        updateWallState({
          settings: localSettings,
          sessions: updatedSessions,
        }).catch(console.error)
      }
    }
  }, [localSettings]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timerRef = spinTimerRef
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        // If unmounting while spinning, clear the stuck state
        updateWallState({ isSpinning: false }).catch(console.error)
      }
    }
  }, [])

  useEffect(() => {
    const q = query(collectionRef('attendees'), orderBy('checkedInAt', 'desc'))
    return listenToCollection(q, (data) => setAllCheckins(data), {
      map: (doc) => {
        const d = doc.data()
        return {
          id: doc.id,
          name: d.displayName || '',
          pictureUrl: d.pictureUrl,
          maritalStatus: d.maritalStatus,
        }
      },
    })
  }, [])

  const handleSaveSettings = async () => {
    if (!wallState) return
    const currentId = wallState.currentSessionId || 'default'
    const updatedSessions = (wallState.sessions || []).map((s) => {
      if (s.id === currentId) {
        return { ...s, settings: localSettings }
      }
      return s
    })

    await updateWallState({
      settings: localSettings,
      sessions: updatedSessions,
    })
    toast.success('บันทึกการตั้งค่าแล้ว')
  }

  const handleSwitchSession = async (sessionId: string | null) => {
    if (!sessionId || !wallState) return
    const targetSession = (wallState.sessions || []).find(
      (s) => s.id === sessionId,
    )
    if (!targetSession) return

    await updateWallState({
      currentSessionId: sessionId,
      settings: targetSession.settings,
      winner: null,
    })
    toast.success(`สลับไปยังเซสชัน: ${targetSession.name}`)
  }

  const handleAddSession = () => {
    setShowAddSessionModal(true)
  }

  const executeAddSession = async (name: string) => {
    if (!wallState) return

    const newSessionId = `session_${Date.now()}`
    const newSession = {
      id: newSessionId,
      name: name.trim(),
      createdAt: Date.now(),
      settings: { ...wallState.settings },
    }

    const updatedSessions = [...(wallState.sessions || []), newSession]

    await updateWallState({
      sessions: updatedSessions,
      currentSessionId: newSessionId,
      settings: newSession.settings,
      winner: null,
    })
    toast.success(`เพิ่มเซสชัน "${name}" สำเร็จ`)
  }

  const handleRenameSession = (sessionId: string, currentName: string) => {
    setRenameSessionId(sessionId)
    setRenameSessionName(currentName)
    setShowRenameSessionModal(true)
  }

  const executeRenameSession = async (sessionId: string, name: string) => {
    if (!wallState) return
    if (!name || !name.trim()) return

    const updatedSessions = (wallState.sessions || []).map((s) => {
      if (s.id === sessionId) {
        return { ...s, name: name.trim() }
      }
      return s
    })

    await updateWallState({ sessions: updatedSessions })
    toast.success(`แก้ไขชื่อเซสชันสำเร็จ`)
  }

  const handleDeleteSession = (sessionId: string, sessionName: string) => {
    if (!wallState) return
    const sessions = wallState.sessions || []
    if (sessions.length <= 1) {
      toast.error('ไม่สามารถลบเซสชันสุดท้ายได้')
      return
    }

    confirm({
      title: 'ยืนยันการลบเซสชัน',
      description: `คุณต้องการลบเซสชัน "${sessionName}" หรือไม่? รายชื่อผู้ชนะในเซสชันนี้จะถูกลบออกทั้งหมด`,
      icon: <Trash className="size-6 text-rose-500" />,
      confirmText: 'ลบเซสชัน',
      confirmVariant: 'danger',
      onConfirm: async () => {
        await executeDeleteSession(sessionId)
      },
    })
  }

  const executeDeleteSession = async (sessionId: string) => {
    if (!wallState) return
    const sessions = wallState.sessions || []

    const updatedSessions = sessions.filter((s) => s.id !== sessionId)
    const updatedWinners = (wallState.winners || []).filter(
      (w) => w && (w.sessionId || 'default') !== sessionId,
    )

    let nextSessionId = wallState.currentSessionId
    let nextSettings = wallState.settings
    if (wallState.currentSessionId === sessionId) {
      const firstRemaining = updatedSessions[0]
      nextSessionId = firstRemaining.id
      nextSettings = firstRemaining.settings
    }

    await updateWallState({
      sessions: updatedSessions,
      winners: updatedWinners,
      currentSessionId: nextSessionId,
      settings: nextSettings,
      winner: null,
    })
    toast.success(`ลบเซสชันสำเร็จ`)
  }

  if (!wallState) return <div className="p-8 text-center">กำลังโหลด...</div>

  const maritalFilter = localSettings.maritalStatusFilter ?? []
  const filteredCheckins =
    maritalFilter.length > 0
      ? allCheckins.filter((g) => {
          const m = g.maritalStatus || 'prefer_not_to_say'
          return maritalFilter.includes(m)
        })
      : allCheckins

  const currentSession = (wallState.sessions || []).find(
    (s) => s.id === (wallState.currentSessionId || 'default'),
  ) || { id: 'default', name: 'เซสชันที่ 1' }

  const activeWinners = (wallState.winners ?? []).filter(
    (w) => w && (w.sessionId || 'default') === currentSession.id,
  )

  return (
    <>
      <Tabs
        defaultValue="control"
        className="bg-background-light flex flex-1 flex-col overflow-hidden"
      >
        <header className="bg-white px-8 pt-6 pb-6 shadow-xs">
          <div className="mx-auto flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                  จอภาพของแขก
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  จัดการ Wall และสุ่มผู้ชนะ
                </p>
              </div>
              <Button
                onClick={openDisplay}
                variant="outline"
                className="text-primary border-primary/20 bg-primary/5 hover:bg-primary/10 h-12 gap-2 rounded-xl px-4 font-bold transition-all active:scale-95"
              >
                <ExternalLink className="size-4" /> เปิดหน้าจอแสดงผล (Projector)
              </Button>
            </div>
            <TabsList className="bg-muted/50 h-auto w-full rounded-xl p-1 md:w-auto">
              <TabsTrigger
                value="control"
                className="data-[state=active]:text-primary gap-2 rounded-lg px-6 py-2.5 font-bold transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <UserRoundSearch className="size-4" /> ควบคุม
              </TabsTrigger>
              <TabsTrigger
                value="winners"
                className="data-[state=active]:text-primary gap-2 rounded-lg px-6 py-2.5 font-bold transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <Trophy className="size-4" /> ผู้ชนะ
              </TabsTrigger>
              <TabsTrigger
                value="settings"
                className="data-[state=active]:text-primary gap-2 rounded-lg px-6 py-2.5 font-bold transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                ตั้งค่า
              </TabsTrigger>
            </TabsList>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto px-8 py-6">
          <div className="mx-auto max-w-7xl space-y-4">
            {/* Premium Session Manager Card */}
            <Card className="rounded-2xl border-rose-100 bg-gradient-to-r from-rose-50 to-amber-50 shadow-sm">
              <CardContent className="flex flex-col items-center justify-between gap-4 p-6 sm:flex-row">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-rose-500 p-2.5 text-white shadow-md">
                    <Layers className="size-5 animate-pulse" />
                  </div>
                  <div>
                    <div className="text-xs font-bold tracking-wider text-rose-500 uppercase">
                      เซสชันสุ่มผู้ชนะที่กำลังทำงาน
                    </div>
                    <div className="flex items-center gap-2 text-xl font-bold text-gray-900">
                      {currentSession.name}
                      <button
                        onClick={() =>
                          handleRenameSession(
                            currentSession.id,
                            currentSession.name,
                          )
                        }
                        className="p-1 text-gray-400 transition-colors hover:text-rose-600"
                        title="แก้ไขชื่อเซสชัน"
                      >
                        <Pencil className="size-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex w-full items-center gap-3 sm:w-auto">
                  <div className="flex-1 sm:flex-initial">
                    <Select
                      value={wallState.currentSessionId || 'default'}
                      onValueChange={handleSwitchSession}
                    >
                      <SelectTrigger className="h-11 w-full rounded-xl border-rose-200 bg-white font-bold sm:w-[220px]">
                        <SelectValue placeholder="เลือกเซสชัน">
                          {currentSession.name}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        {(wallState.sessions || []).map((s) => (
                          <SelectItem
                            key={s.id}
                            value={s.id}
                            className="font-medium"
                          >
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleAddSession}
                    className="h-11 rounded-xl bg-rose-500 px-4 font-bold text-white shadow-md shadow-rose-500/10 hover:bg-rose-600"
                  >
                    <Plus className="mr-1.5 size-4" />
                    เพิ่มเซสชัน
                  </Button>

                  {(wallState.sessions || []).length > 1 && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        handleDeleteSession(
                          currentSession.id,
                          currentSession.name,
                        )
                      }
                      className="h-11 rounded-xl border-rose-200 font-bold text-rose-500 hover:bg-rose-50 hover:text-rose-600"
                      title="ลบเซสชันปัจจุบัน"
                    >
                      <Trash className="size-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <WallControlTab
              wallState={wallState}
              filteredCheckins={filteredCheckins}
              allCheckins={allCheckins}
              localSettings={localSettings}
              spinTimerRef={spinTimerRef}
            />
            <WallWinnersTab
              winners={activeWinners}
              currentSessionId={currentSession.id}
              allWinners={wallState.winners ?? []}
            />
            <WallSettingsTab
              localSettings={localSettings}
              setLocalSettings={setLocalSettings}
              onSave={handleSaveSettings}
            />
          </div>
        </main>
      </Tabs>
      <AddSessionModal onAdd={executeAddSession} />
      <RenameSessionModal
        initialName={renameSessionName}
        onRename={(name) => executeRenameSession(renameSessionId, name)}
      />
      <ConfirmModal />
    </>
  )
}
